import { useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { savePredictions, saveBankerGameId } from '../storage'
import { getNextCycleGameId } from '../predictionCycle'
import { resolveBankerToggle } from '../bankerSwitch'
import { useAutosave } from './usePersistedState'
import type { Game, Prediction } from '../types'

/** Only the fields the banker handlers actually read, so callers can pass a fixture or a stub. */
type BankerCandidate = { id: string; homeTeam: string; awayTeam: string }

export interface UsePredictionsOptions {
  currentGameweek: number
  games: Game[]
  /** Locks every mutation once the shared deadline has passed. */
  isDeadlinePassed: boolean
  isLoading: boolean
  onToast: (message: string) => void
  /** Called when the "Predict Now" hero starts the cycle — see `useHasStartedGw`. */
  onStartGameweek: () => void
  /** Called once a prediction the user actually changed has been persisted — the
   * install nudge's trigger. Fires on the save landing, not on the tap, and never on the load
   * echo; see `useAutosave`'s `onSaved`. */
  onPredictionSaved?: () => void
}

export interface PredictionsController {
  predictions: Record<string, Prediction>
  setPredictions: Dispatch<SetStateAction<Record<string, Prediction>>>
  bankerGameId: string | null
  setBankerGameId: Dispatch<SetStateAction<string | null>>
  /** The fixture whose edit sheet is open, or null. */
  editingGameId: string | null
  /** True while stepping through every fixture in one pass (the "Predict all N" flow). */
  isCycling: boolean
  /** True once a second Banker tap has armed a switch and is awaiting confirmation. */
  isBankerSwitchPending: boolean
  openGame: (gameId: string) => void
  changeScore: (gameId: string, homeScore: number, awayScore: number) => void
  startCycle: () => void
  /** Same walk as `startCycle`, entered partway: the sheet opens on `gameId` carrying the
   *  Next button, so finishing a half-called gameweek is the same gesture as calling a fresh one. */
  startCycleAt: (gameId: string) => void
  nextGame: () => void
  closeEdit: () => void
  toggleBanker: (game: BankerCandidate) => void
  confirmBankerSwitch: (game: BankerCandidate) => void
  cancelBankerSwitch: () => void
}

/**
 * Score predictions, the Banker pick, and the edit-sheet cycle that walks
 * them. Both slices are keyed by gameweek server-side, so both autosave under `currentGameweek`
 * and every call site that changes the gameweek sets the new gameweek's values in the same commit
 * — which is why neither needs the reload/ref-guard pair that quest picks and reveal-seen do.
 */
export function usePredictions({
  currentGameweek,
  games,
  isDeadlinePassed,
  isLoading,
  onToast,
  onStartGameweek,
  onPredictionSaved,
}: UsePredictionsOptions): PredictionsController {
  const [predictions, setPredictions] = useState<Record<string, Prediction>>({})
  const [bankerGameId, setBankerGameId] = useState<string | null>(null)
  const [editingGameId, setEditingGameId] = useState<string | null>(null)
  const [isCycling, setIsCycling] = useState(false)
  const [isBankerSwitchPending, setIsBankerSwitchPending] = useState(false)

  /**
   * Never write back a locked gameweek.
   *
   * Every mutation below already refuses once the deadline has passed, so the only writes this
   * suppresses are echoes: the poll hands this hook the server's own predictions, the value changes
   * identity, and the autosave `PUT`s them straight back — into a route that answers 409 because
   * the gameweek is locked, surfacing "Failed to save your predictions." for something the user
   * never did. Rare enough to look like a fluke while the shared phase changed two or three times a
   * gameweek; the phase moves every time a fixture finishes, so the same echo
   * would toast all afternoon.
   */
  const notLocked = () => !isDeadlinePassed

  useAutosave({
    value: predictions,
    scope: currentGameweek,
    enabled: !isLoading,
    guard: notLocked,
    save: (value) => savePredictions(currentGameweek, value),
    onError: () => onToast('Failed to save your predictions.'),
    onSaved: onPredictionSaved,
  })

  useAutosave({
    value: bankerGameId,
    scope: currentGameweek,
    enabled: !isLoading,
    guard: notLocked,
    save: (value) => saveBankerGameId(currentGameweek, value),
    onError: () => onToast('Failed to save your Banker pick.'),
  })

  const openGame = (gameId: string) => {
    if (isDeadlinePassed) return
    // Seed an unpredicted fixture at 0-0 so the sheet opens on a real prediction rather than an
    // empty one — the autosave then persists it immediately.
    setPredictions((prev) => {
      if (prev[gameId]) return prev
      return { ...prev, [gameId]: { gameId, homeScore: 0, awayScore: 0 } }
    })
    setEditingGameId(gameId)
    setIsBankerSwitchPending(false)
  }

  const startCycleAt = (gameId: string) => {
    if (isDeadlinePassed || !games.some((game) => game.id === gameId)) return
    setIsCycling(true)
    onStartGameweek()
    openGame(gameId)
  }

  const closeEdit = () => {
    setIsCycling(false)
    setEditingGameId(null)
    setIsBankerSwitchPending(false)
  }

  return {
    predictions,
    setPredictions,
    bankerGameId,
    setBankerGameId,
    editingGameId,
    isCycling,
    isBankerSwitchPending,
    openGame,
    closeEdit,

    changeScore: (gameId, homeScore, awayScore) => {
      if (isDeadlinePassed) return
      setPredictions((prev) => ({ ...prev, [gameId]: { gameId, homeScore, awayScore } }))
    },

    startCycle: () => {
      if (games.length === 0) return
      startCycleAt(games[0].id)
    },

    startCycleAt,

    nextGame: () => {
      if (!editingGameId) return
      const nextGameId = getNextCycleGameId(games, editingGameId)
      if (!nextGameId) {
        closeEdit()
        return
      }
      openGame(nextGameId)
    },

    toggleBanker: (game) => {
      switch (resolveBankerToggle(bankerGameId, game.id, isDeadlinePassed)) {
        case 'noop':
          return
        case 'armSwitch':
          setIsBankerSwitchPending(true)
          return
        case 'set':
          setBankerGameId(game.id)
          return
        case 'remove':
          setBankerGameId(null)
          setIsBankerSwitchPending(false)
      }
    },

    confirmBankerSwitch: (game) => {
      setBankerGameId(game.id)
      setIsBankerSwitchPending(false)
    },

    cancelBankerSwitch: () => setIsBankerSwitchPending(false),
  }
}
